import React, { useEffect, useRef, useState } from 'react';
import { Button, Alert, Spin } from 'antd';
import { AudioOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import mobileService from '../../../../services/mobileService';

/**
 * Lecteur du memo vocal joint a une declaration mobile.
 *
 * Le fichier vit dans OssanGED ; le sinistre n'en porte que la reference
 * (`memoAudioGedRef`). On le telecharge en blob puis on le lit localement : servir
 * l'URL GED directement au <audio> ne marcherait pas, la GED exige le header
 * Authorization que la balise n'envoie pas.
 *
 * Chargement a la demande (bouton) et non au montage : un memo vocal pese quelques
 * centaines de Ko et la plupart des consultations d'un dossier ne l'ecoutent pas.
 */
export default function MemoVocal({ gedRef, compact = false }) {
  const { t } = useTranslation('dossierSinistre');
  const [blobUrl, setBlobUrl] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const urlRef = useRef(null);

  // Revoque l'object URL au demontage : sans cela le blob reste en memoire tant
  // que l'onglet est ouvert, a chaque dossier consulte.
  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  if (!gedRef) return null;

  const charger = async () => {
    setChargement(true);
    setErreur(null);
    try {
      const blob = await mobileService.telechargerDocument(gedRef);
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setBlobUrl(url);
    } catch (err) {
      setErreur(err?.response?.data?.message || t('memoVocal.illisible'));
    } finally {
      setChargement(false);
    }
  };

  if (erreur) {
    return <Alert type="warning" showIcon message={erreur} style={{ fontSize: 12 }} />;
  }

  if (!blobUrl) {
    return (
      <Button
        icon={chargement ? <Spin size="small" /> : <AudioOutlined />}
        onClick={charger}
        disabled={chargement}
        size={compact ? 'small' : 'middle'}
      >
        {chargement ? t('memoVocal.chargement') : t('memoVocal.ecouter')}
      </Button>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- pas de transcription : c'est un enregistrement libre de l'usager */}
      <audio controls src={blobUrl} style={{ height: 36, maxWidth: '100%' }} />
      <Button
        size="small"
        type="text"
        icon={<DownloadOutlined />}
        href={blobUrl}
        download={`memo-vocal-${gedRef}.m4a`}
      >
        {t('memoVocal.telecharger')}
      </Button>
    </div>
  );
}
